// shared-lib/labor/qualifiers.js
//
// A QUALIFIER is one fact about a counted thing that changes how it is priced
// (claude-docs/2026-10-06-pricing-variants-design.md): a large-format tile is
// slower to set, a freestanding tub is a different install, a pocket door is
// a frame as well as a door. Each is read here from what the design ALREADY
// records, so a variant prices itself — nobody picks it by hand.
//
// One axis per fact, and the base value of each axis carries NO qualifier
// (an alcove tub, a wall-mount filler, a swing door): a row with no qualifier
// prices exactly as it did before variants existed. Unknown is the base too —
// a field nobody set is not evidence of a variant. Pure.

const { deviceWorkItemFor } = require('./deviceTypes');

const { EFFECTS, AXIS_DECLARATIONS, axisOf } = require('./axes');
const { isUpperFloor } = require('./floorLevel');
const { hoodFormOf } = require('../fixture-types/hoodForm');
const { getSinkType, isKitchenSink } = require('../sink-types');
const { systemPanSizing } = require('../shower-systems');

// Every axis, its values and its words are DECLARED in `axes.js` (with the
// effect that decides what an unpriced value carries). These are the tables
// the rest of the code has always read, derived from the declarations.

/** The axes, in the order a variant key joins its values. */
const AXES = AXIS_DECLARATIONS.map((a) => a.key);

/** Each axis's values (smallest first for an ordered axis); the base is never listed. */
const VALUES = Object.fromEntries(
  AXIS_DECLARATIONS.map((a) => [a.key, [...a.values]])
);

/** How each qualifier reads to a person. */
const QUALIFIER_WORDS = Object.fromEntries(
  AXIS_DECLARATIONS.map((a) => [a.key, { ...a.words }])
);

/**
 * The words that name each value in a contractor's line, for the importer:
 * `[axis, value, pattern, reads]`, axes in `AXES` order (`axes.js`
 * `synonyms`). `reads: 'all'` reads the description and cost group too.
 */
const QUALIFIER_SYNONYMS = AXIS_DECLARATIONS.flatMap((a) =>
  a.synonyms.map((x) => [a.key, x.value, x.pattern, x.reads])
);

/**
 * The words that name an axis at its base — the band below one value of an
 * ordered axis, or the base value of another — for the importer: `[axis,
 * below, pattern]` ("Shower Pan up to 60\"" states a pan's size, the base
 * below `over-60x36`; "Frameless Shower Glass" a shower glass's framing, the base
 * beside `semi-frameless`).
 */
const BASE_SYNONYMS = AXIS_DECLARATIONS.flatMap((a) =>
  a.baseSynonyms.map((x) => [a.key, x.below, x.pattern])
);

/** An axis's effect — 'look' | 'extra' | 'job' — or null for a name that is not an axis. */
const effectOf = (axis) => axisOf(axis)?.effect ?? null;

/** Is this axis a size band, its values ordered smallest first? */
const isOrdered = (axis) => axisOf(axis)?.ordered === true;

/**
 * Does this axis's variant add work to its base (so an add-on may ride on
 * it)? Every axis but a different JOB's (`install`, `pan`, `found`): a device
 * swapped where one stands, a prefab pan set, an acrylic pan torn out are not
 * their base with something added, so their price for the other job is not
 * this one's, and no add-on rides on it (`resolveQualifiedWork`). A look adds
 * nothing, but keeps the answer it always had.
 */
const addsWork = (axis) => effectOf(axis) !== EFFECTS.JOB;

const M_TO_IN = 39.3701;
// V3: large format = a piece whose longest side is over 24"
const LARGE_FORMAT_LONGEST_IN = 24;
// "up to 60x36": a shower pan's longer side over 60" or shorter side over 36"
const PAN_LONG_IN = 60;
const PAN_SHORT_IN = 36;

const SHOWERS = new Set([
  'shower',
  'corner-shower',
  'straight-sliding-door-shower',
]);
const TUBS = new Set(['bathtub', 'tub']);
const TUB_FILLERS = new Set(['bathroom-tub-faucet', 'tub-faucet']);
const HOODS = new Set(['vent-hood', 'range-hood']);
// what a gas connection serves (`fuel`): the cooking appliances whose
// catalog products record a fuel
const COOKING = new Set(['range', 'stove', 'cooktop']);
// a bathroom sink by the mount its `sinkType` names (2026-10-25); a vanity's
// undermount or drop-in, and a sink that does not say, is the base
const LAV_SINKS = new Set(['sink', 'bathroom-sink']);
const BASIN_OF_SINK_TYPE = Object.freeze({
  pedestal: 'pedestal',
  'wall-mount': 'wall-mount',
  vessel: 'vessel',
});
const basinOf = (obj) =>
  isKitchenSink(obj) ? null : (BASIN_OF_SINK_TYPE[getSinkType(obj)] ?? null);
// a toilet hung on the wall, by its product's `mountType` ('wall-hung')
const toiletMountOf = (obj) =>
  /^wall/i.test(String(obj?.mountType ?? '')) ? 'wall-hung' : null;
const VALVE_PARTS = new Set([
  'showerhead',
  'shower-control',
  'shower-controls',
  'shower-stem',
]);

const finite = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * The ONE rule for "sold as a stone slab" — its pieces, layout, seams, edges,
 * cutouts, its stone type and the catalog import's size check all ask it.
 * Here, below both `labor` and `estimate-pricing` (which re-exports it from
 * `utils/slabFinish.js`), because `labor` never imports `estimate-pricing`.
 */
const isSlabFinish = (material) =>
  material?.type === 'slab' || material?.caseUnit === 'slab';

// a material that is laid as a glued SHEET, not planks
const SHEET_MATERIALS = new Set(['sheet vinyl', 'sheet-vinyl', 'linoleum']);

/**
 * Is this vinyl finish a SHEET floor — sold by the roll (its package,
 * `caseUnit`, or a `dimensions` side recorded as 'roll', as the catalog
 * records a roll good), or of a sheet material — rather than planks or tiles?
 */
const isSheetVinyl = (doc) =>
  String(doc?.type || '').toLowerCase() === 'vinyl' &&
  (doc.caseUnit === 'roll' ||
    doc.dimensions?.length === 'roll' ||
    doc.dimensions?.width === 'roll' ||
    SHEET_MATERIALS.has(String(doc.material ?? '').toLowerCase()));

// the stones a slab's `material` names that are a `stone` value of their own;
// quartz, marble and any other stone are the base
const STONE_VALUES = new Set(axisOf('stone').values);

/**
 * A finish's qualifiers: `{ format: 'large' }` for a large-format tile piece,
 * `{ format: 'mosaic' }` for a mosaic,
 * `{ stone: 'quartzite' }` or `{ stone: 'granite' }` for a slab whose
 * `material` is one, `{ floor: 'sheet-vinyl' }` for a vinyl floor laid as a
 * glued sheet (`isSheetVinyl`). Stone is read only from a slab finish — never
 * from a name or a tile's tag.
 */
function qualifiersForTexture(doc) {
  if (!doc) return {};
  if (isSlabFinish(doc)) {
    const stone = String(doc.material ?? '').toLowerCase();
    return STONE_VALUES.has(stone) ? { stone } : {};
  }
  if (isSheetVinyl(doc)) return { floor: 'sheet-vinyl' };
  // a mosaic is set on its sheet, at a rate of its own (NCE 2026 p.289) — and
  // its pieces are small, so never large format (the sheet's size is not)
  if (doc.isMosaic) return { format: 'mosaic' };
  if (String(doc.type || '').toLowerCase() !== 'tile') return {};
  const d = doc.dimensions || {};
  const sides = [d.width, d.length ?? d.height]
    .map(finite)
    .filter((n) => n !== null);
  if (!sides.length) return {};
  return Math.max(...sides) > LARGE_FORMAT_LONGEST_IN
    ? { format: 'large' }
    : {};
}

/** `{ size: 'over-60x36' }` for a shower whose pan is bigger, else `{}`. */
function showerSizeOf(obj) {
  const x = finite(obj.dimensions?.x);
  const z = finite(obj.dimensions?.z);
  if (x === null || z === null) return {};
  // to the whole inch, as a pan is sized: a scan that reads a 60x36 pan as
  // 60.3 x 36.2 has measured the standard pan, not a bigger one
  const long = Math.round(Math.max(x, z) * M_TO_IN);
  const short = Math.round(Math.min(x, z) * M_TO_IN);
  return long > PAN_LONG_IN || short > PAN_SHORT_IN
    ? { size: 'over-60x36' }
    : {};
}

// a mirror's bands: fits within 2′×3′ (the base), within 3′×5′, or bigger
const MIRROR_BASE_IN = { short: 24, long: 36 };
const MIRROR_MID_IN = { short: 36, long: 60 };

/**
 * A mirror's install band from its width × height (`dimensions.x` and `.y`,
 * metres), each rounded to the whole inch — as a shower pan is read — and
 * sorted, so a mirror turned on its side is the same band: null when it fits
 * within 2′×3′ (or its size is unknown), `'over-2x3'` within 3′×5′, else
 * `'over-3x5'`.
 */
function mirrorBandOf(obj) {
  const x = finite(obj?.dimensions?.x);
  const y = finite(obj?.dimensions?.y);
  if (x === null || y === null) return null;
  const [short, long] = [Math.round(x * M_TO_IN), Math.round(y * M_TO_IN)].sort(
    (a, b) => a - b
  );
  if (short <= MIRROR_BASE_IN.short && long <= MIRROR_BASE_IN.long) return null;
  if (short <= MIRROR_MID_IN.short && long <= MIRROR_MID_IN.long)
    return 'over-2x3';
  return 'over-3x5';
}

// a shower niche's bands by its longest side, and the widest it is before it
// crosses a stud: 16″ on centre leaves a 14½″ bay
const NICHE_SINGLE_IN = 16;
const NICHE_TWO_TIER_IN = 28;
const STUD_BAY_IN = 14.5;

/**
 * A shower niche's size band from its width × height (`dimensions.x` and
 * `.y`), its longest side to the whole inch: null within 16″ (or its size
 * unknown), `'over-16in'` within 28″, else `'over-28in'`.
 */
function nicheBandOf(obj) {
  const sides = [obj?.dimensions?.x, obj?.dimensions?.y].map(finite);
  if (sides.some((n) => n === null)) return null;
  const longest = Math.round(Math.max(...sides) * M_TO_IN);
  if (longest <= NICHE_SINGLE_IN) return null;
  return longest <= NICHE_TWO_TIER_IN ? 'over-16in' : 'over-28in';
}

/** `'horizontal'` for a niche wider than a stud bay (to the half inch), else null. */
function nicheOrientationOf(obj) {
  const width = finite(obj?.dimensions?.x);
  if (width === null) return null;
  return Math.round(width * M_TO_IN * 2) / 2 > STUD_BAY_IN
    ? 'horizontal'
    : null;
}

/** Is this a shower whose pan (`showerPan`) the estimate reads? */
const readsShowerPan = (obj) => SHOWERS.has(obj?.type);

/**
 * Is this a shower niche whose build (`nicheBuild`) the estimate reads —
 * framed and tiled (the base, the field cleared) or a prefab insert
 * (`'prefab'`: acrylic, solid surface, metal; 2026-10-28)?
 */
const readsNicheBuild = (obj) => obj?.type === 'shower-niche';

/**
 * The tile a TILED niche's back is set in when it is not the wall's — a
 * mosaic chosen on the niche's panel (`nicheBackTextureId`, 2026-10-28) — or
 * null. A prefab insert has no tile back, whatever the field still holds.
 */
const nicheBackTextureOf = (obj) =>
  readsNicheBuild(obj) && obj.nicheBuild !== 'prefab'
    ? (obj.nicheBackTextureId ?? null)
    : null;

/** `{ pan: 'prefab' }` for a shower set on a prefab pan; tiled is the base. */
const showerPanOf = (obj) =>
  obj.showerPan === 'prefab' ? { pan: 'prefab' } : {};

const tubTypeOf = (raw) => {
  const t = String(raw || '').toLowerCase();
  if (t === 'dropin' || t === 'drop-in') return 'drop-in';
  if (t === 'freestanding' || t === 'undermount') return t;
  if (t === 'walk-in' || t === 'walkin') return 'walk-in';
  return null; // alcove, and unknown
};

const fillerMountOf = (obj) => {
  if (obj.wallMounted === true) return null; // wall is the base
  const raw = String(obj.faucetMountType || obj.mountType || '').toLowerCase();
  if (['floor', 'floormount', 'floor-mount', 'freestanding'].includes(raw))
    return 'floor';
  if (['deck', 'deckmount', 'deck-mount'].includes(raw)) return 'deck';
  return null;
};

// the hood's form as its mount: a plain canopy is the base
const HOOD_MOUNT = { chimney: 'chimney', island: 'island', canopy: null };

/** A hood's mount from the build the design draws (`hoodFormOf`). */
const hoodMountOf = (obj) => HOOD_MOUNT[hoodFormOf(obj?.dimensions)];

/** A recessed can: a ceiling light drawn as one, or the legacy type. */
const isRecessedLight = (obj) =>
  obj?.type === 'recessed-light' ||
  (obj?.type === 'ceiling-light' && obj.proceduralStyle === 'recessed');

// The light fixtures the take-off installs (`takeoffMapping.UNIT_MAP`'s
// lighting block): a ceiling light of any kind, a pendant, a wall light.
const LIGHT_TYPES = new Set([
  'ceiling-light',
  'recessed-light',
  'pendant-light',
  'wall-light',
  'overhead-wall-light',
  'sconce',
]);

/** Is this a light fixture (any ceiling, pendant or wall light)? */
const isLightFixture = (obj) => LIGHT_TYPES.has(obj?.type);

/**
 * Whether an object may be marked as replacing one where it stands — the
 * panel's "Replaces an existing one" box: an electrical device, or a light
 * (a can's retrofit since 2026-10-15; any light, hung where one hangs, since
 * 2026-10-25).
 */
const canReplaceExisting = (obj) =>
  Boolean(obj) && (Boolean(deviceWorkItemFor(obj)) || isLightFixture(obj));

/** A ceiling light's kind, for its install: a recessed can or a chandelier. */
const lightKindOf = (obj) => {
  if (isRecessedLight(obj)) return 'recessed';
  return obj?.type === 'ceiling-light' && obj.lightingSubtype === 'chandelier'
    ? 'chandelier'
    : null;
};

/** An outlet set into a counter or a floor — the drawing's surface outlet. */
const SURFACE_OUTLET = 'surface-outlet';

/** Is this an electrical device (`deviceTypes.js`)? It answers `install` only. */
const isDevice = (obj) => Boolean(deviceWorkItemFor(obj));

/** A valve part in a TUB's fixture group carries a diverter. */
const valveInTubGroup = (obj, roomObjects) => {
  const group = obj.fixtureGroupId;
  return (
    !!group &&
    (roomObjects || []).some(
      (o) => TUBS.has(o?.type) && o.fixtureGroupId === group
    )
  );
};

/** Is this a part of a shower valve (a head, a control, a stem)? */
const readsValveTrim = (obj) => VALVE_PARTS.has(obj?.type);

/**
 * Does this valve part's shower keep its valve — its own `trimOnly`, or any
 * valve part of its fixture group's? The box is ticked on ONE part of a
 * shower whose head and control are separate objects, and the shower is one
 * install, so every part reads the same answer.
 */
const trimOnlyOf = (obj, roomObjects) => {
  if (obj?.trimOnly === true) return true;
  const group = obj?.fixtureGroupId;
  return (
    !!group &&
    (roomObjects || []).some(
      (o) =>
        readsValveTrim(o) && o.fixtureGroupId === group && o.trimOnly === true
    )
  );
};

/**
 * The OBJECT readers: one per axis an object can carry, each returning one
 * declared value of its axis, or null for the base or unknown
 * (claude-docs/2026-10-21-standard-variations-design.md, "The readers"). In
 * `AXES` order. A device answers `install` — whatever else it holds, it is
 * never read as a shower, a tub or a can — so every other reader leaves
 * devices out, but for one that reads a device by its TYPE (a surface
 * outlet's `outlet`). Later axes append a reader here.
 */
const OBJECT_READERS = Object.freeze([
  {
    axis: 'size',
    appliesTo: (obj) => !isDevice(obj) && SHOWERS.has(obj.type),
    read: (obj) => showerSizeOf(obj).size ?? null,
  },
  {
    // a mirror's install band, by its size either way round
    axis: 'size',
    appliesTo: (obj) => !isDevice(obj) && obj.type === 'mirror',
    read: mirrorBandOf,
  },
  {
    // a shower niche's band, by its longest side
    axis: 'size',
    appliesTo: (obj) => !isDevice(obj) && obj.type === 'shower-niche',
    read: nicheBandOf,
  },
  {
    // a shower niche set as a prefab insert (its panel's switch)
    axis: 'niche',
    appliesTo: (obj) => !isDevice(obj) && readsNicheBuild(obj),
    read: (obj) => (obj.nicheBuild === 'prefab' ? 'prefab' : null),
  },
  {
    // …and a tiled niche's back set in a tile of its own, a mosaic (its
    // panel's Back pick; the tile itself is billed as wall tile)
    axis: 'accent',
    appliesTo: (obj) => !isDevice(obj) && readsNicheBuild(obj),
    read: (obj) => (nicheBackTextureOf(obj) ? 'mosaic-back' : null),
  },
  {
    // a shower system's molded seat (step 3a), set floating on the wall
    axis: 'seat',
    appliesTo: (obj) => !isDevice(obj) && obj.type === 'shower-seat',
    read: () => 'floating',
  },
  {
    // the bench `create_bathroom_shower` builds, framed and tiled in
    axis: 'seat',
    appliesTo: (obj) => !isDevice(obj) && obj.type === 'shower-bench',
    read: () => 'tiled',
  },
  {
    axis: 'tub',
    appliesTo: (obj) => !isDevice(obj) && TUBS.has(obj.type),
    read: (obj) => tubTypeOf(obj.tubType),
  },
  {
    axis: 'mount',
    appliesTo: (obj) => !isDevice(obj) && TUB_FILLERS.has(obj.type),
    read: (obj) => fillerMountOf(obj),
  },
  {
    // a hood with a chimney cover, or hung over an island (2026-10-25)
    axis: 'mount',
    appliesTo: (obj) => !isDevice(obj) && HOODS.has(obj.type),
    read: hoodMountOf,
  },
  {
    axis: 'mount',
    appliesTo: (obj) => !isDevice(obj) && obj.type === 'toilet',
    read: toiletMountOf,
  },
  {
    axis: 'basin',
    appliesTo: (obj) => !isDevice(obj) && LAV_SINKS.has(obj.type),
    read: basinOf,
  },
  {
    axis: 'valve',
    appliesTo: (obj) => !isDevice(obj) && VALVE_PARTS.has(obj.type),
    read: (obj, roomObjects) =>
      valveInTubGroup(obj, roomObjects) ? 'diverter' : null,
  },
  {
    // a recessed can, or a chandelier (2026-10-25, its lighting subtype)
    axis: 'light',
    appliesTo: (obj) => !isDevice(obj) && isLightFixture(obj),
    read: lightKindOf,
  },
  {
    // a device, or a light (a can's retrofit, any light hung where one
    // hangs), the user marked as replacing one where it stands — new is the
    // base, so an unmarked one carries nothing
    axis: 'install',
    appliesTo: canReplaceExisting,
    read: (obj) => (obj.replacesExisting === true ? 'replace' : null),
  },
  {
    axis: 'pan',
    appliesTo: (obj) => !isDevice(obj) && SHOWERS.has(obj.type),
    read: (obj) => showerPanOf(obj).pan ?? null,
  },
  {
    // a tub in a room on an upper floor (`floorLevel.js`). Read only where
    // the take-off knows the room's storey and the scan's setting (`place`);
    // a caller with no place reads the base, as before.
    axis: 'story',
    appliesTo: (obj) => !isDevice(obj) && TUBS.has(obj.type),
    read: (obj, roomObjects, place) =>
      place && isUpperFloor(place) ? 'upper' : null,
  },
  {
    // a shower valve that stays: only its trim is set
    axis: 'retrofit',
    appliesTo: (obj) => !isDevice(obj) && VALVE_PARTS.has(obj.type),
    read: (obj, roomObjects) =>
      trimOnlyOf(obj, roomObjects) ? 'trim-only' : null,
  },
  {
    // a shower niche across a stud
    axis: 'orientation',
    appliesTo: (obj) => !isDevice(obj) && obj.type === 'shower-niche',
    read: nicheOrientationOf,
  },
  {
    // an outlet set into a counter or a floor (2026-10-25): a device that
    // answers more than `install`, by its type alone
    axis: 'outlet',
    appliesTo: (obj) => obj?.type === SURFACE_OUTLET,
    read: () => 'popup',
  },
  {
    // a kitchen sink with an apron front, by the mount its `subtype` names
    // (2026-10-27) — the field the kitchen renderer draws, not `sinkType`,
    // which a generic pick used to stamp beside it
    axis: 'apron',
    appliesTo: (obj) => !isDevice(obj) && isKitchenSink(obj),
    read: (obj) => (obj.subtype === 'farmhouse' ? 'farmhouse' : null),
  },
  {
    // a kitchen sink of two bowls, by its `basinCount` (2026-10-27)
    axis: 'bowls',
    appliesTo: (obj) => !isDevice(obj) && isKitchenSink(obj),
    read: (obj) => (obj.basinCount === 2 ? 'double' : null),
  },
  {
    // a system pan of a size its maker does not make (shower systems 4b)
    axis: 'sizing',
    appliesTo: (obj) => !isDevice(obj) && SHOWERS.has(obj.type),
    read: (obj) => systemPanSizing(obj),
  },
]);

/**
 * An object's qualifiers, reading the room's other objects where a fact needs
 * them, and its PLACE — `{ story, lowestFloor }`, the room's storey and the
 * scan's floor-level setting — where one does. No place reads as the base.
 */
function qualifiersForObject(obj, roomObjects = [], place = null) {
  if (!obj) return {};
  const out = {};
  for (const reader of OBJECT_READERS) {
    if (!reader.appliesTo(obj)) continue;
    const value = reader.read(obj, roomObjects, place);
    if (value != null) out[reader.axis] = value;
  }
  return out;
}

// the fuels that put a gas line on a range or cooktop: a dual-fuel range
// cooks on gas over an electric oven
const GAS_FUELS = new Set(['gas', 'dual-fuel', 'propane', 'natural-gas', 'lp']);

/**
 * What an object's CATALOG product says that the object itself does not
 * (2026-10-25): `{ fuel: 'gas' }` for a range or cooktop whose product's
 * `fuelType` burns gas, and a hood's `mount` from its product's `mountType`
 * ('island') or `subtype` ('chimney'). `{}` for no product, or one that says
 * none of it — unknown is the base. Merged OVER the object's own
 * (`qualifiersForObject`) where the take-off resolves the product: the
 * product is what is drawn and bought.
 */
function qualifiersForModel(type, model) {
  if (!model) return {};
  if (COOKING.has(type)) {
    const fuel = String(model.fuelType ?? '').toLowerCase();
    return GAS_FUELS.has(fuel) ? { fuel: 'gas' } : {};
  }
  if (HOODS.has(type)) {
    if (String(model.mountType ?? '').toLowerCase() === 'island')
      return { mount: 'island' };
    if (String(model.subtype ?? '').toLowerCase() === 'chimney')
      return { mount: 'chimney' };
  }
  return {};
}

/**
 * The job's answer about the shower its demolition takes out
 * (`structure.asFoundAnswers.showerPan`, Schedules & Takeoffs' "Existing
 * shower"): unanswered is a TILED shower — a mortar-bed pan under tiled walls;
 * `acrylic` an acrylic pan under tiled walls; `unit` a one-piece fiberglass or
 * acrylic unit, its pan and walls one piece (2026-10-25). One field for the
 * one fact, what shower was there; `labor/demoScope.js` reads it.
 */
const SHOWER_FOUND = Object.freeze({ ACRYLIC: 'acrylic', UNIT: 'unit' });

const acrylicPanOf = (answers) =>
  answers?.showerPan === SHOWER_FOUND.ACRYLIC ? { found: 'acrylic-pan' } : {};

/**
 * What a demolition line tears out, from the job's answers about what was
 * there before (`structure.asFoundAnswers` — not the scanned as-found rooms;
 * claude-docs/2026-10-07-shower-pans-plan.md).
 * A scan cannot see behind a surface, so these are asked, never measured; an
 * unanswered job is the base (a tiled pan) and prices as before.
 */
const AS_FOUND = {
  'demo-shower-pan': acrylicPanOf,
  'demo-shower-stall': acrylicPanOf,
};

/** A hand-entered or scan-derived scope line's qualifiers — `{}` for most. */
const qualifiersForScope = (workItemId, asFoundAnswers) =>
  AS_FOUND[workItemId]?.(asFoundAnswers) ?? {};

// the widest a door is before it is a WIDE one (`width` axis): a 30″ door
// is the passage width, a 32″ one the first wide one
const WIDE_DOOR_OVER_IN = 30;

/** A door's operation, from its `category.door.doorType`; null for a swing door. */
function doorKindOf(doorType) {
  // a barn value contains "Sliding" (it is a surface-mounted slider), so Barn
  // is read first
  const t = String(doorType || '');
  if (t.includes('Barn')) return 'barn';
  if (t.includes('Bifold')) return 'bifold';
  if (t.includes('Pocket')) return 'pocket';
  if (t.includes('Sliding')) return 'sliding';
  if (t.includes('French')) return 'french';
  return null;
}

/**
 * A door's qualifiers: its operation, from its `category.door.doorType`, and
 * — given its width in metres — `width: 'over-30in'` for a door wider than
 * 30″, at the door schedule's own whole inch (a 30″ door measured a hair
 * over is still one). No width, or none given, says nothing of it.
 */
function qualifiersForDoor(doorType, widthM = null) {
  const door = doorKindOf(doorType);
  const w = finite(widthM);
  const wide = w !== null && Math.round(w * M_TO_IN) > WIDE_DOOR_OVER_IN;
  return {
    ...(door ? { door } : {}),
    ...(wide ? { width: 'over-30in' } : {}),
  };
}

/** `{ valve:'diverter', format:'large' }` → `'format=large&valve=diverter'` (axis order). */
const qualifierSignature = (q) =>
  AXES.filter((axis) => q?.[axis])
    .map((axis) => `${axis}=${q[axis]}`)
    .join('&');

/** The inverse of `qualifierSignature`. */
const parseSignature = (sig) =>
  Object.fromEntries(
    String(sig || '')
      .split('&')
      .filter(Boolean)
      .map((pair) => pair.split('='))
  );

/** "large format", "over 60×36, with diverter" — '' for none. */
const describeQualifiers = (q) =>
  AXES.filter((axis) => q?.[axis])
    .map((axis) => QUALIFIER_WORDS[axis]?.[q[axis]] ?? q[axis])
    .join(', ');

module.exports = {
  AXES,
  VALUES,
  QUALIFIER_WORDS,
  QUALIFIER_SYNONYMS,
  BASE_SYNONYMS,
  addsWork,
  effectOf,
  isOrdered,
  OBJECT_READERS,
  isSlabFinish,
  isSheetVinyl,
  qualifiersForTexture,
  qualifiersForObject,
  qualifiersForModel,
  qualifiersForDoor,
  qualifiersForScope,
  SHOWER_FOUND,
  isRecessedLight,
  isLightFixture,
  canReplaceExisting,
  readsShowerPan,
  readsNicheBuild,
  nicheBackTextureOf,
  readsValveTrim,
  fillerMountOf,
  trimOnlyOf,
  mirrorBandOf,
  qualifierSignature,
  parseSignature,
  describeQualifiers,
};
